import type { ReactNode } from "react";
import { useCountUp } from "./useInstrument";

/* =====================================================================
   The parts every instrument shares: the device frame, its registration
   marks, the scan sweep, and a telemetry readout.
   ===================================================================== */

export interface Readout {
  value: string;
  label: string;
}

export function InstrumentBar({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="inst-bar">
      <span className="inst-bar-l">{title}</span>
      {children ? <span className="inst-bar-r">{children}</span> : null}
    </div>
  );
}

export function PlayButton({
  playing,
  atEnd,
  onClick,
  label,
}: {
  playing: boolean;
  atEnd: boolean;
  onClick: () => void;
  /** What the control does, for anyone not reading the icon. */
  label: string;
}) {
  return (
    <button type="button" className="inst-btn" onClick={onClick} aria-label={label}>
      <svg width="9" height="10" viewBox="0 0 9 10" aria-hidden="true">
        {playing ? (
          <path d="M0 0h3v10H0zM6 0h3v10H6z" fill="currentColor" />
        ) : (
          <path d="M0 0l9 5-9 5z" fill="currentColor" />
        )}
      </svg>
      <span>{playing ? "Pause" : atEnd ? "Replay" : "Play"}</span>
    </button>
  );
}

function TelemetryValue({ value, reduced }: { value: string; reduced: boolean }) {
  const shown = useCountUp(value, reduced);
  return <b>{shown}</b>;
}

export function Telemetry({
  readouts,
  hot,
  reduced,
}: {
  readouts: readonly Readout[];
  /** Index of the readout that carries the point of the stage. */
  hot?: number;
  reduced: boolean;
}) {
  return (
    <div className="inst-tele">
      {readouts.map((r, i) => (
        <div key={r.label} className={`inst-t${i === hot ? " is-hot" : ""}`}>
          {/* Keyed on the value so a stage change remounts and re-counts
              rather than animating from the previous stage's number. */}
          <TelemetryValue key={r.value} value={r.value} reduced={reduced} />
          <i>{r.label}</i>
        </div>
      ))}
    </div>
  );
}

export function Tick({ on }: { on: boolean }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ color: on ? "var(--i-accent)" : "var(--i-hair)", flex: "none" }}
    >
      {on ? <path d="M20 6 9 17l-5-5" /> : <path d="M5 12h14" />}
    </svg>
  );
}
